import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { CountryInfo } from '../../../../types/ussd.models';

export interface PhoneInputValue {
  raw: string;
  isValid: boolean;
  formatted: string;
}

let nextId = 0;

/**
 * Country and phone number for the simulator's test handset. The country is
 * a native select (keyboard and screen-reader friendly, no flags); the number
 * formats as you type and accepts paste, including a full +international number.
 */
@Component({
  selector: 'app-phone-input',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.css',
})
export class PhoneInputComponent implements OnChanges {
  @Input() value: string = '';
  @Input() country!: CountryInfo;
  @Input() countries: CountryInfo[] = [];
  @Input() showValidation: boolean = true;
  @Input() disabled: boolean = false;

  @Output() valueChange = new EventEmitter<PhoneInputValue>();
  @Output() countryChange = new EventEmitter<CountryInfo>();

  readonly selectId = `pi-country-${nextId}`;
  readonly inputId = `pi-number-${nextId}`;
  readonly messageId = `pi-message-${nextId++}`;

  displayValue = '';
  isValid: boolean | null = null;
  isFocused = false;
  private lastRaw = '';

  get showInvalid(): boolean {
    return this.showValidation && this.isValid === false && !this.isFocused && this.lastRaw.length > 3;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['country'] && !changes['country'].firstChange && !changes['value']) {
      this.reformat(this.lastRaw);
    }
    if (changes['value']) {
      const val = (changes['value'].currentValue as string | null | undefined) ?? '';
      if (val !== this.lastRaw) this.reformat(val);
    }
  }

  onCountry(code: string): void {
    const next = this.countries.find((c) => c.code === code);
    if (!next) return;
    this.countryChange.emit(next);
    this.lastRaw = '';
    this.displayValue = '';
    this.isValid = null;
    this.valueChange.emit({ raw: '', isValid: false, formatted: '' });
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
    this.reformat(raw);
    input.value = this.displayValue;
    this.valueChange.emit({ raw, isValid: this.isValid ?? false, formatted: this.displayValue });
  }

  private reformat(raw: string): void {
    this.lastRaw = raw;
    if (!raw) {
      this.displayValue = '';
      this.isValid = null;
      return;
    }
    try {
      this.displayValue = new AsYouType(this.country?.code as CountryCode).input(raw);
    } catch {
      this.displayValue = raw;
    }
    if (raw.replace(/\D/g, '').length > 3) {
      try {
        const full = raw.startsWith('+') ? raw : (this.country?.dialCode ?? '') + raw.replace(/^0+/, '');
        this.isValid = isValidPhoneNumber(full);
      } catch {
        this.isValid = false;
      }
    } else {
      this.isValid = null;
    }
  }
}
